import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { api } from "@/lib/api";
import { useSite, siteText } from "@/context/SiteContext";
import { toImageUrl } from "@/lib/driveUrl";
import { MapPin } from "lucide-react";

function dayMonth(dateStr) {
  try {
    return format(new Date(dateStr), "d MMMM", { locale: fr }).toUpperCase();
  } catch {
    return "";
  }
}

export default function AgendaSection({ compact = false }) {
  const [events, setEvents] = useState(null);
  const { settings } = useSite();

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => setEvents([]));
  }, []);

  if (events === null) return null;
  if (compact && events.length === 0) return null;

  return (
    <section data-testid="agenda-section" className={compact ? "" : "border-b border-stone-200 bg-[#FAF6F0] py-20"}>
      <div className={compact ? "" : "mx-auto max-w-6xl px-4 sm:px-6"}>
        <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div>
            <span className="text-xs font-bold uppercase tracking-widest text-[#B83227]">{siteText(settings, "agenda_label", "En Direct")}</span>
            <h2 className="mt-1 font-display text-3xl font-bold uppercase text-[#1E1B18] sm:text-4xl">
              {siteText(settings, "agenda_title", "Prochaines Dates")}
            </h2>
          </div>
          <Link to="/contact" data-testid="agenda-invite-link" className="text-xs font-bold uppercase text-[#B83227] hover:underline">
            {siteText(settings, "agenda_invite", "Inviter le groupe sur votre date →")}
          </Link>
        </div>

        {events.length === 0 ? (
          <p className="rounded-xl border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-stone-500">
            Les prochaines dates seront annoncées ici très bientôt.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {events.map((ev, i) => (
              <div key={ev.id} data-testid={`agenda-card-${i}`} className="space-y-4 overflow-hidden rounded-xl border border-stone-200 bg-white shadow-sm transition-transform duration-300 hover:-translate-y-1">
                {ev.image_url && (
                  <img src={toImageUrl(ev.image_url)} alt={`Affiche — ${ev.title}`} loading="lazy" onError={(e) => (e.currentTarget.style.display = "none")} className="h-40 w-full object-cover" />
                )}
                <div className={`space-y-4 ${ev.image_url ? "px-6 pb-6" : "p-6"}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`rounded px-2.5 py-1 text-[11px] font-bold uppercase text-white ${ev.free_entry ? "bg-[#B83227]" : "bg-[#E67E22]"}`}>
                    {ev.free_entry ? "Entrée libre" : ev.price_info || "Payant"}
                  </span>
                  <span className="text-xs font-bold text-stone-500">{dayMonth(ev.date)}</span>
                </div>
                <h3 className="font-display text-xl font-bold uppercase text-[#1E1B18]">{ev.title}</h3>
                <p className="flex items-center gap-1.5 text-xs text-stone-600">
                  <MapPin className="h-3.5 w-3.5 shrink-0 text-[#B83227]" aria-hidden="true" />
                  {[ev.location, ev.time].filter(Boolean).join(" · ") || "Détails à venir"}
                </p>
                {ev.description && <p className="text-xs leading-relaxed text-stone-600">{ev.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
